import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Spinner } from '../atoms/Spinner.js';
import { get, ApiClientError } from '../../lib/api-client.js';
import type { ApiResponse } from '../../types/api.js';
import type { InterviewSession } from '../../types/interview.js';

interface SurveyGuardProps {
  children: ReactNode;
}

type GuardState =
  | { type: 'loading' }
  | { type: 'valid' }
  | { type: 'expired' }
  | { type: 'responded' }
  | { type: 'not_found' };

export function SurveyGuard({ children }: SurveyGuardProps) {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<GuardState>({ type: 'loading' });

  useEffect(() => {
    async function check() {
      if (!token) {
        setState({ type: 'not_found' });
        return;
      }
      try {
        const res = await get<ApiResponse<InterviewSession>>(`/api/v1/interviews/${token}/status`);

        if (res.data.status === 'completed') {
          setState({ type: 'responded' });
        } else {
          setState({ type: 'valid' });
        }
      } catch (err) {
        if (err instanceof ApiClientError) {
          if (err.code === 'ALREADY_RESPONDED') {
            setState({ type: 'responded' });
          } else if (err.status === 404) {
            setState({ type: 'not_found' });
          } else if (err.status === 410 || err.code === 'EXPIRED') {
            setState({ type: 'expired' });
          } else {
            setState({ type: 'not_found' });
          }
        } else {
          setState({ type: 'not_found' });
        }
      }
    }

    void check();
  }, [token]);

  switch (state.type) {
    case 'loading':
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      );
    case 'valid':
      return <>{children}</>;
    case 'expired':
      return <Navigate to="/expired" replace />;
    case 'responded':
      return <Navigate to="/already-responded" replace />;
    case 'not_found':
      return <Navigate to="/not-found" replace />;
  }
}
